/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LINT_UNUSED_FABROOM
 * Date:         2025-12-31
 * Modified By:  Cursor
 *
 * Description:
 * Removed unused faBroom import from fontawesome icons.
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/components/MobileUsersList.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_UI_SPLIT_MOBILE_REFINED
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refined mobile list view to resolve compilation issues.
 */

import * as React from "react";
import { Card, Form, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faUserSecret } from "@fortawesome/free-solid-svg-icons"; // [2026-06-23] impersonation: added faUserSecret
import { AdminUser } from "../../../../interfaces/adminUsers";
import { formatUserForDisplay } from "../../../../services/adminUsersService";
import { UsersTablePagination } from "./UsersTablePagination";
import AdminButton from "../../../../components/admin/AdminButton";
import styles from "../../AdminUsers.module.scss";

interface MobileUsersListProps {
  data: AdminUser[];
  selected: string[];
  onRowSelect: (id: string, checked: boolean) => void;
  onEdit: (id: string) => void;
  onImpersonate?: (id: string) => void;
  onPreviewWorkingData?: (userId: string, userName: string, userEmail?: string) => void;
  selectedUserForCleanup?: string | null;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  formatDate: (date?: string) => string;
}

export function MobileUsersList({
  data,
  selected,
  onRowSelect,
  onEdit,
  onImpersonate,
  onPreviewWorkingData,
  selectedUserForCleanup,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  formatDate,
}: MobileUsersListProps) {
  return (
    <div>
      <div className="d-flex flex-column gap-3">
        {data.map((user) => {
          const display = formatUserForDisplay(user);
          const isSelected = selected.includes(user._id);
          const isCleaning = selectedUserForCleanup === user._id;

          return (
            <Card key={user._id} className={isSelected ? "border-primary" : ""}>
              <Card.Body>
                <div className="d-flex align-items-start gap-3">
                  <Form.Check
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => onRowSelect(user._id, e.target.checked)}
                    aria-label={`Select ${display.displayName}`}
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      {/* [2026-07-09] impersonation-admin: name links to View as User for ALL targets (admins included, for debugging) */}
                      <h6 className="mb-0">
                        {onImpersonate ? (
                          <button
                            type="button"
                            className={styles['user-name-link']}
                            onClick={() => onImpersonate(user._id)}
                            title="Act as user"
                          >
                            {display.displayName}
                          </button>
                        ) : (
                          display.displayName
                        )}
                      </h6>
                      <div className="d-flex gap-2">
                        <AdminButton
                          intent="secondary"
                          size="sm"
                          onClick={() => onEdit(user._id)}
                          aria-label={`Edit ${display.displayName}`}
                          disabled={isCleaning}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </AdminButton>
                        {/* [2026-07-09] impersonation-admin: View as User available for admin targets too */}
                        {onImpersonate && (
                          <AdminButton
                            intent="secondary"
                            size="sm"
                            onClick={() => onImpersonate(user._id)}
                            title="Act as user"
                            aria-label={`View as ${display.displayName}`}
                            disabled={isCleaning}
                          >
                            <FontAwesomeIcon icon={faUserSecret} />
                          </AdminButton>
                        )}
                        {onPreviewWorkingData && (
                          <AdminButton
                            intent="secondary"
                            size="sm"
                            onClick={() => onPreviewWorkingData(user._id, display.displayName, user.email)}
                            title="Preview working data"
                            disabled={isCleaning}
                          >
                            {isCleaning ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faEye} />
                            )}
                          </AdminButton>
                        )}
                      </div>
                    </div>
                    <p className="text-muted small mb-2">{user.email}</p>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg={user.isAdmin ? "primary" : "secondary"}>
                        {display.displayRole}
                      </Badge>
                      <Badge
                        bg={
                          (user.status || "active") === "active"
                            ? "success"
                            : (user.status || "active") === "suspended"
                              ? "danger"
                              : "warning"
                        }
                      >
                        {display.displayStatus}
                      </Badge>
                      <Badge bg={user.email_verified === "Y" ? "success" : "secondary"}>
                        {display.displayVerified}
                      </Badge>
                    </div>
                    <div className="mt-2 text-muted small">
                      <div>Last Login: {display.displayLastLogin}</div>
                      <div>Registered: {formatDate(user.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      <UsersTablePagination
        total={total}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </div>
  );
}
